import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download, Trash2, Upload } from "lucide-react";

const TITLES = {
  debtors: ["Step 3 — Import Debtors", "One settleable Opening Balance Document per row. Mode: SUMMARY (one balance per client), BUCKET (per ageing band) or DOCUMENT (per legacy invoice). Negative amount = customer advance."],
  creditors: ["Step 4 — Import Creditors", "Vendor side — strict Vendor Code matching. Positive = payable (appears in Bill Payments queue), negative = advance paid."],
  inventory: ["Step 5 — Import Inventory", "Opening stock per Item × Warehouse at certified valuation. Creates opening batches; periodic GL gets one Inventories (Opening) line."],
  assets: ["Step 6 — Import Assets", "Pre-filled with the legacy FAR assets (no PI linkage). Fill gross value, capitalisation date and accumulated depreciation as on cutoff."],
  bank: ["Step 7 — Import Bank & Cash", "Pre-filled with Bank Ledger masters + CASH rows per branch. Signed balance: positive = Dr, negative = overdraft/Cr."],
  other: ["Step 8 — Import Other Balances", "Share Capital, Reserves, Loans, GST, TDS, Advances, Deposits and all remaining Balance Sheet ledgers. Ledgers must exist in the Ledger Master; P&L ledgers are rejected."],
};

export const ObImportStep = ({ batch, category, locked, onChanged }) => {
  const [summary, setSummary] = useState((batch.steps || {})[category] || null);
  const [exceptions, setExceptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [title, subtitle] = TITLES[category];

  const loadErrors = useCallback(() => {
    api.get(`/opening-balance/batch/${batch.id}/rows/${category}`, { params: { status: "error", limit: 200 } })
      .then((r) => setExceptions(r.data.rows || [])).catch(() => {});
  }, [batch.id, category]);
  useEffect(() => { loadErrors(); }, [loadErrors]);
  useEffect(() => { setSummary((batch.steps || {})[category] || null); }, [batch, category]);

  async function downloadTemplate() {
    setBusy(true);
    try {
      const r = await api.get(`/opening-balance/batch/${batch.id}/template/${category}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `opening_${category}_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
    finally { setBusy(false); }
  }

  async function onUpload(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/opening-balance/batch/${batch.id}/import/${category}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(r.data);
      toast[r.data.rows_error ? "warning" : "success"](
        `${r.data.rows_valid} valid, ${r.data.rows_error} error(s)`);
      onChanged();
      loadErrors();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed", { duration: 8000 });
    } finally { setBusy(false); }
  }

  async function clearImport() {
    setBusy(true);
    try {
      await api.delete(`/opening-balance/batch/${batch.id}/import/${category}`);
      setSummary(null);
      setExceptions([]);
      toast.success("Import cleared");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <div className="bg-white border border-slate-200" data-testid={`ob-import-${category}`}>
      <div className="px-5 py-3.5 border-b border-slate-100">
        <div className="text-sm font-bold text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
      </div>
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid={`ob-${category}-template`} onClick={downloadTemplate} disabled={busy}
            className="border border-slate-300 text-slate-700 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:border-[#F97316] hover:text-[#F97316] disabled:opacity-50">
            <Download size={12} /> Download Template
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden"
            data-testid={`ob-${category}-file`} onChange={onUpload} />
          <button data-testid={`ob-${category}-upload`} onClick={() => fileRef.current?.click()}
            disabled={busy || locked}
            className="bg-orange-600 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-orange-700 disabled:opacity-50">
            <Upload size={12} /> {busy ? "Working…" : "Upload Filled Excel"}
          </button>
          {summary?.rows_total > 0 && !locked && (
            <button data-testid={`ob-${category}-clear`} onClick={clearImport} disabled={busy}
              className="border border-rose-200 text-rose-600 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-rose-50 disabled:opacity-50">
              <Trash2 size={12} /> Clear
            </button>
          )}
        </div>

        {summary?.rows_total > 0 && (
          <div className="grid grid-cols-4 gap-2 max-w-2xl" data-testid={`ob-${category}-summary`}>
            {[["Rows", summary.rows_total], ["Valid", summary.rows_valid],
              ["Errors", summary.rows_error],
              ["Total", formatINR(summary.total_amount || 0)]].map(([k, v], i) => (
              <div key={k} className={`border px-3 py-2 ${i === 2 && summary.rows_error > 0 ? "border-rose-200 bg-rose-50/50" : "border-slate-100"}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{k}</div>
                <div className={`font-black tabular-nums ${i === 1 ? "text-emerald-700" : i === 2 && summary.rows_error > 0 ? "text-rose-700" : "text-slate-800"}`}>{v}</div>
              </div>
            ))}
          </div>
        )}
        {summary?.file_name && (
          <div className="text-[11px] text-slate-500">
            File: <b>{summary.file_name}</b> · uploaded {String(summary.uploaded_at || "").slice(0, 16).replace("T", " ")} by {summary.uploaded_by}
            {summary.rows_error === 0 && summary.rows_valid > 0 && (
              <span className="ml-2 text-emerald-600 font-bold inline-flex items-center gap-1"><CheckCircle2 size={12} /> Step complete</span>
            )}
          </div>
        )}

        {exceptions.length > 0 && (
          <div className="border border-rose-200" data-testid={`ob-${category}-exceptions`}>
            <div className="px-3 py-2 bg-rose-50 text-[10px] uppercase tracking-wider font-bold text-rose-700">
              Exception Report — fix these rows in the Excel and re-upload
            </div>
            <div className="max-h-64 overflow-y-auto">
              {exceptions.map((r) => (
                <div key={r.id} className="px-3 py-1.5 border-t border-rose-50 text-[11px] flex gap-2">
                  <AlertTriangle size={12} className="text-rose-500 mt-0.5 shrink-0" />
                  <span><b>Row {r.row_number}</b> — {(r.errors || []).join("; ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
